/**
 * AI Voice Editing — backend contract for POST /api/Report/EditReportWithAI.
 *
 * Everything that depends on the backend contract lives in this file, so it can
 * be adjusted in one place once Vigen confirms the details:
 *   - request fields (confirmed from EDIT_REPORT_AI_DEMO.js)
 *   - response envelope (confirmed: operations, needsClarification,
 *     clarificationMessage, warnings, totalUsedTokens)
 *   - operation fields: only replace_text {find, replace} is confirmed.
 *     The other actions are read through OPERATION_FIELDS below; an operation
 *     whose fields are not found is skipped and reported, never guessed at.
 *   - the permission code (PROCESS_AI until a dedicated one exists).
 */
import { CRE_PERMISSION } from "../crePermissions";

export const EDIT_REPORT_AI_ENDPOINT = "/api/Report/EditReportWithAI";
export const EDIT_REPORT_AI_PROMPT_TYPE = "cre_edit_report_ai_prompt";
export const EDIT_REPORT_AI_SOURCE_APP = "CRE";

/**
 * Vigen (21 Oct): the server enforces no permission code yet; when gating is
 * turned on, EditReportWithAI uses PROCESS_AI like ProcessWithAI. The client
 * uses the same PROCESS_AI check as the Process AI button.
 */
export const EDIT_REPORT_AI_PERMISSION = CRE_PERMISSION.PROCESS_AI;

/**
 * Speech shorter than this (in words) is not sent: in AI Edit the mic stays
 * open, and single words / noise ("No", "OK") would otherwise reach the AI.
 * Real instructions always have a verb and a target; undo/redo run locally.
 */
export const MIN_INSTRUCTION_WORDS = 2;

/** Pause after the last spoken segment before the instruction is sent. */
export const EDIT_INSTRUCTION_PAUSE_MS = 1800;

/**
 * Jacky (20 Oct): replace/delete every matching occurrence by default; only
 * inside a section/paragraph when the physician names one (the API then sends
 * `sectionId` or the paragraph text). No confirmation. So the client applies
 * every match within the operation's scope, whatever `replaceAll` says.
 */
export const APPLY_TO_ALL_MATCHES_IN_SCOPE = true;

/**
 * Vigen (21 Oct): append_text always adds a NEW paragraph / list item at the
 * end of the section, never merges into existing content (prompt rule; the
 * applier always creates a new block node).
 */
export const APPEND_AS_NEW_PARAGRAPH_BY_DEFAULT = true;

/**
 * "New line after every sentence": in the CRE editor a visible line is a
 * paragraph (Process AI writes one <p> per sentence, paragraphs have 0.2em
 * spacing, each line has its own drag handle and lang/dir, and Shift+Enter
 * hard breaks are disabled). So each sentence becomes its own paragraph with
 * the same attributes/formatting; inside a numbered item it stays in that item.
 * Headings are never split. ("line_break" = <br> is kept as an alternative.)
 */
export const FORMAT_SENTENCES_MODE = "paragraph";

/**
 * Raw operation fields per action, first match wins. Confirmed from live
 * responses (20 Oct, 15 calls): every action can carry `scope`
 * ("section" | "entire_report"), `sectionId` (heading text), `matchType`
 * ("exact" = case-sensitive), `replaceAll`, and a free-text `rule`.
 * `find` / `text` / `replace` can be plain text or block HTML.
 *
 * Internal shapes (see applyEditOperations.js):
 *   replace  { find, replace, all, section, exact }
 *   delete   { find, all, section, exact }
 *   append   { text, section, asNewParagraph, atStart }
 *   insert   { text, anchor, position }            (no anchor → append)
 *   move     { find, section, exact, rule, destination }
 *   format_sentences { section, scope, rule, mode }
 */
const SECTION_FIELDS = ["sectionId", "section", "sectionTitle", "heading"];
const COMMON = {
  section: SECTION_FIELDS,
  scope: ["scope"],
  matchType: ["matchType"],
  rule: ["rule"],
  // The API's own promise; the client enforces it (see applyEditOperations).
  preserveHeadings: ["preserveHeadings"],
};

export const OPERATION_FIELDS = {
  replace_text: { kind: "replace", ...COMMON, find: ["find"], replace: ["replace"], all: ["replaceAll", "all"] },
  delete_content: { kind: "delete", ...COMMON, find: ["find", "target", "text"], all: ["replaceAll", "deleteAll", "all"] },
  append_text: { kind: "append", ...COMMON, text: ["text", "content", "append"], position: ["position"] },
  // Live (20 Oct): "after X add Y" → { action: "insert_text", find: "<p>X</p>",
  // text: "<p>Y</p>" } — the anchor comes as `find`, no position (= after).
  insert_text: {
    kind: "insert",
    ...COMMON,
    text: ["text", "content", "insert"],
    anchor: ["anchor", "find", "after", "before"],
    position: ["position"],
  },
  move_text: {
    kind: "move",
    ...COMMON,
    find: ["find", "text"],
    targetSection: ["targetSectionId", "targetSection"],
    anchor: ["anchor", "after", "before"],
    position: ["position"],
  },
  format_sentences: { kind: "format_sentences", ...COMMON },
};

const REQUIRED = {
  replace: ["find", "replace"],
  delete: ["find"],
  append: ["text"],
  insert: ["text", "anchor"],
  move: ["find"],
  format_sentences: [],
};

/**
 * move_text destination. The API gives it only as free text in `rule`
 * (seen: "move_to_end_of_section", "Move the matched paragraph to the
 * beginning of the FINDINGS section, before the first H4 subsection.",
 * "Move this paragraph to immediately after <p …>…</p>",
 * 'Move the sentence immediately after "X." within the … subsection.',
 * "Move immediately after: X.") plus
 * `targetSectionId`. Only these patterns are recognised; anything else is
 * reported as unsupported. TO CONFIRM with Vigen: structured fields.
 */
export function parseMoveDestination({ rule, targetSection, anchor, position }) {
  // Structured destination (proposed to Vigen 21 Oct, after the prompt update
  // dropped the position from `rule`): position "start" | "end" | "before" |
  // "after", with `anchor` = exact text for before/after.
  const pos = String(position || "").trim().toLowerCase();
  if (typeof anchor === "string" && anchor.trim()) {
    return { where: pos === "before" ? "before" : "after", anchor };
  }
  if (pos === "start" || pos === "beginning" || pos === "top") {
    return { where: "section_start", section: targetSection || null };
  }
  if (pos === "end" || pos === "bottom") {
    return { where: "section_end", section: targetSection || null };
  }
  const text = String(rule || "").trim();
  // after/before + a quoted sentence or block HTML, anywhere in the rule
  // (live: '…immediately after "X." within the … subsection.').
  const anchored = text.match(
    /\b(?:immediately\s+)?(after|before)\s+(<([a-z][a-z0-9]*)\b[^>]*>[\s\S]*?<\/\3>|"[^"]+"|“[^”]+”|‘[^’]+’)/i,
  );
  if (anchored) {
    const target = anchored[2].replace(/^["“‘]|["”’]$/g, "");
    return { where: anchored[1].toLowerCase(), anchor: target };
  }
  // Unquoted after a colon (live: "Move immediately after: X."): the rest of
  // the rule is the anchor sentence. If it is not in the report the op is skipped.
  const colon = text.match(/\b(?:immediately\s+)?(after|before)\s*:\s*(\S[\s\S]*)$/i);
  if (colon) {
    return { where: colon[1].toLowerCase(), anchor: colon[2].trim() };
  }
  if (/move_to_(start|beginning|top)_of_section|\b(beginning|start|top)\b|תחילת/i.test(text)) {
    return { where: "section_start", section: targetSection || null };
  }
  if (/move_to_end_of_section|\b(end|bottom)\b|סוף/i.test(text)) {
    return { where: "section_end", section: targetSection || null };
  }
  // Vigen (21 Oct): sectionId = source, targetSectionId = destination; the
  // destination is no longer put in `rule`. A target section alone = its end.
  if (typeof targetSection === "string" && targetSection.trim()) {
    return { where: "section_end", section: targetSection };
  }
  return null;
}

/** format_sentences mode: always FORMAT_SENTENCES_MODE (the API rule wording varies by language). */
export function formatModeFromRule() {
  return FORMAT_SENTENCES_MODE;
}

function pick(raw, names) {
  for (const name of names || []) {
    if (raw[name] !== undefined && raw[name] !== null) return { name, value: raw[name] };
  }
  return null;
}

function newRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `edit-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Request body, field names exactly as in EDIT_REPORT_AI_DEMO.js.
 */
export function buildEditReportRequest({
  email,
  userId,
  relatedUserId,
  organisationId,
  studyId,
  templateId,
  language,
  modelSpeed,
  currentReportHtml,
  instruction,
  requestId = newRequestId(),
}) {
  return {
    email: email || "",
    userID: userId || "",
    organisationId: organisationId || null,
    studyId: studyId || null,
    relatedUserId: relatedUserId || userId || "",
    templateId: templateId || null,
    language: language || "en",
    modelSpeed: modelSpeed || undefined,
    promptTypeName: EDIT_REPORT_AI_PROMPT_TYPE,
    sourceApp: EDIT_REPORT_AI_SOURCE_APP,
    requestId,
    currentReportHtml: currentReportHtml || "",
    voiceInstructions: instruction || "",
  };
}

/**
 * Raw API operation → internal op, or { unsupported } with the reason.
 */
export function normalizeOperation(raw) {
  if (!raw || typeof raw !== "object") return { unsupported: "invalid", raw };
  const action = String(raw.action || raw.type || "").trim();
  const spec = OPERATION_FIELDS[action];
  if (!spec) return { unsupported: "unknown_action", action, raw };

  const op = { kind: spec.kind, action };
  for (const [key, names] of Object.entries(spec)) {
    if (key === "kind") continue;
    const hit = pick(raw, names);
    if (!hit) continue;
    if (key === "anchor" && (hit.name === "after" || hit.name === "before")) {
      op.anchor = hit.value;
      if (!op.position) op.position = hit.name;
      continue;
    }
    op[key] = hit.value;
  }
  op.exact = String(op.matchType || "").toLowerCase() === "exact";
  op.preserveHeadings = op.preserveHeadings === true;
  delete op.matchType;
  if (op.scope === "entire_report") delete op.section;

  // insert_text without an anchor = add to a section (scope "section") or the report end.
  if (op.kind === "insert" && !(typeof op.anchor === "string" && op.anchor.trim())) {
    op.kind = "append";
    delete op.anchor;
  }

  // Live (22 Oct): "Remove the X section" → delete_content with only
  // scope "section" + sectionId (no find) = delete that whole section.
  if (
    op.kind === "delete" &&
    !(typeof op.find === "string" && op.find.trim()) &&
    op.scope === "section" &&
    typeof op.section === "string" &&
    op.section.trim()
  ) {
    op.wholeSection = true;
    delete op.find;
    return op;
  }

  for (const key of REQUIRED[op.kind]) {
    if (typeof op[key] !== "string" || (key !== "replace" && !op[key].trim())) {
      return { unsupported: "missing_fields", action, raw };
    }
  }

  if (op.kind === "replace" || op.kind === "delete") {
    // The API marks "do it everywhere" requests with replaceAll: true; when such
    // a request finds nothing it is "nothing to do", not an error.
    op.softMiss = op.all === true;
    op.all = APPLY_TO_ALL_MATCHES_IN_SCOPE ? true : op.all !== false;
  }
  if (op.kind === "append") {
    op.asNewParagraph = APPEND_AS_NEW_PARAGRAPH_BY_DEFAULT;
    op.atStart = op.position === "start" || op.position === "beginning";
    delete op.position;
  }
  if (op.kind === "insert") op.position = op.position === "before" ? "before" : "after";
  if (op.kind === "move") {
    const destination = parseMoveDestination(op);
    if (!destination) return { unsupported: "unknown_destination", action, raw };
    op.destination = destination;
    delete op.targetSection;
    delete op.anchor;
    delete op.position;
  }
  if (op.kind === "format_sentences") op.mode = formatModeFromRule(op.rule);
  return op;
}

/**
 * API response (`{ success, payload }` or the payload itself) → UI-ready result.
 */
export function normalizeEditResponse(data) {
  const p = (data && (data.payload || data.Payload)) || data || {};
  const rawOps = Array.isArray(p.operations) ? p.operations : [];
  const operations = [];
  const unsupported = [];
  for (const raw of rawOps) {
    const op = normalizeOperation(raw);
    if (op.unsupported) unsupported.push(op);
    else operations.push(op);
  }
  return {
    operations,
    unsupported,
    needsClarification: p.needsClarification === true,
    clarificationMessage:
      typeof p.clarificationMessage === "string" ? p.clarificationMessage.trim() : "",
    warnings: Array.isArray(p.warnings) ? p.warnings.filter((w) => typeof w === "string" && w.trim()) : [],
    totalUsedTokens: Number.isFinite(p.totalUsedTokens) ? p.totalUsedTokens : null,
  };
}
